// src/shared/dashboard/shell/Sidebar.tsx
//
// The Claude-style left sidebar. Most products use it as their primary desktop
// navigation; an assigned university course uses it as a contextual table of
// contents while stable workspace destinations move to the top menu. The
// account cluster stays pinned to the bottom. Collapsible state persists per
// device.
import { useState } from "react";
import {
  BookOpen,
  LogOut,
  GraduationCap,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
} from "lucide-react";
import { useT } from "@/shared/i18n/useT";
import type { NavSection } from "@/shared/dashboard/shell/nav";
import { itemMatchesPath } from "@/shared/dashboard/shell/nav";
import { Icon } from "@/shared/dashboard/shell/Icon";
import {
  navGroupLabel,
  navLabel,
  INSTRUCTOR_LABEL,
  STUDIO_LABEL,
} from "@/shared/dashboard/shell/navLabels";
import { go } from "@/shared/dashboard/shell/navigate";
import {
  ProductSwitcher,
  LogoMark,
} from "@/shared/dashboard/shell/ProductSwitcher";
import { LanguageSelect } from "@/shared/dashboard/shell/LanguageSelect";
import { useStatusChip } from "@/shared/dashboard/shell/statusChip";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";

const COLLAPSED_KEY = "empatiq.sidebarCollapsed";

const ROW =
  "relative flex w-full min-h-11 cursor-pointer items-center gap-3 rounded-[6px] px-3 text-left transition-colors duration-200 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ";

// Icon-only row for the collapsed rail — square, centered, tooltip on hover.
const ICON_ROW =
  "group relative mx-auto grid h-11 w-11 cursor-pointer place-items-center rounded-[6px] transition-colors duration-200 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ";

function rowTone(active: boolean): string {
  return active
    ? "bg-[var(--rail-bg-active)] text-[var(--rail-active)] before:absolute before:left-0 before:h-5 before:w-[3px] before:rounded-r-full before:bg-[var(--rail-signal)]"
    : "text-[var(--rail-text)] hover:bg-[var(--rail-hover-bg)] hover:text-[var(--rail-hover-text)]";
}

/** Hover label for collapsed icon rows (the rail is too thin for text). */
function Tooltip({ label }: { label: string }) {
  return (
    <span className="pointer-events-none absolute left-[120%] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap bg-[var(--ink)] px-2.5 py-1 ed-mono text-[10px] text-[var(--paper)] opacity-0 transition group-hover:opacity-100">
      {label}
    </span>
  );
}

export function Sidebar({
  sections,
  activeId,
  pathname,
  locale,
  isInstructor = false,
  verticalId,
  userInitial,
  userEmail,
  hideAccountStatus = false,
  showWorkspaceSwitcher = true,
  courseContext,
  onSectionNavigate,
  onSignOut,
}: {
  sections: NavSection[];
  activeId: string;
  pathname: string;
  locale: string;
  isInstructor?: boolean;
  verticalId?: string;
  userInitial: string;
  userEmail?: string;
  hideAccountStatus?: boolean;
  showWorkspaceSwitcher?: boolean;
  courseContext?: {
    organization: string;
    title: string;
    term: string;
    path: string;
  };
  onSectionNavigate?: (path: string, id: string) => void;
  onSignOut: () => void;
}) {
  const t = useT();
  const status = useStatusChip();
  const { hasAccess, setShowPaywall } = useSubscription();
  const StatusIcon = status.Icon;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !value;
    });
  };
  const toggleLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";
  const consumerNav = verticalId === "general" && !courseContext;
  const openPlan = () => (hasAccess ? go("/profile") : setShowPaywall(true));

  return (
    /* z-40: sticky creates a stacking context, so the picker/language dropdowns
       (z-50 INSIDE it) can only win over page content (hero z-10 etc.) if the
       sidebar's own context sits above the content column. Kept below fixed
       overlays/modals (z-50). */
    <aside
      aria-label="Application navigation"
      className={`hidden shrink-0 flex-col self-stretch border-r border-[var(--rail-hairline)] bg-[var(--rail-bg)] transition-[width] duration-200 lg:sticky lg:top-0 lg:z-40 lg:flex lg:h-dvh ${
        collapsed ? "w-[72px]" : "w-[252px]"
      }`}
    >
      {/* Brand mark + collapse toggle, then the product · workspace picker —
          top-left, where Claude Code puts it. Collapsed: just the toggle. */}
      <div
        className={`shrink-0 ${collapsed ? "px-3 pb-2 pt-5" : "px-4 pb-3 pt-5"}`}
      >
        <div
          className={`mb-5 flex items-center ${collapsed ? "justify-center" : "justify-between"}`}
        >
          {!collapsed && (
            <button
              type="button"
              onClick={() => go("/dashboard")}
              aria-label="EmpatiQ"
              className="flex cursor-pointer items-center px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {/* The rail is always dark navy now — the paper wordmark, always. */}
              <LogoMark className="h-[19px]" tone="paper" />
            </button>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-label={toggleLabel}
            title={toggleLabel}
            className="grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-[6px] text-[var(--rail-text)] transition-colors duration-200 hover:bg-[var(--rail-hover-bg)] hover:text-[var(--rail-hover-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            {collapsed ? (
              <PanelLeftOpen size={16} />
            ) : (
              <PanelLeftClose size={16} />
            )}
          </button>
        </div>
        {!collapsed &&
          (courseContext ? (
            <button
              type="button"
              onClick={() => go(courseContext.path)}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-[6px] border border-[var(--rail-hairline)] px-2.5 py-2 text-left transition-colors duration-200 hover:bg-[var(--rail-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[6px] bg-[var(--rail-bg-active)] text-[var(--rail-active)]">
                <BookOpen size={15} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold leading-tight tracking-[-0.01em] text-[var(--rail-hover-text)]">
                  {courseContext.title}
                </span>
                <span className="mt-1 block truncate ed-mono text-[8px] uppercase tracking-[0.13em] text-[var(--rail-text)]">
                  {courseContext.organization} · {courseContext.term}
                </span>
              </span>
            </button>
          ) : showWorkspaceSwitcher ? (
            <ProductSwitcher variant="sidebar" />
          ) : null)}
      </div>

      {/* Sections. The active section's contextual entries (its rail) indent
          beneath it — hidden while collapsed (icons only). */}
      {/* Collapsed keeps overflow visible so hover tooltips can escape the rail
          (overflow-y-auto would force x-clipping); the icon list is short. */}
      <nav
        aria-label="Primary navigation"
        className={`min-h-0 flex-1 space-y-1 py-3 ${collapsed ? "px-3 overflow-visible" : "px-3 overflow-y-auto"}`}
      >
        {sections.map((section, index) => {
          const active = section.id === activeId;
          const label = navLabel(section.id, section.label, locale);
          const showGroupLabel =
            !!section.group && sections[index - 1]?.group !== section.group;
          if (collapsed) {
            return (
              <button
                key={section.id}
                type="button"
                onClick={() =>
                  onSectionNavigate
                    ? onSectionNavigate(section.path, section.id)
                    : go(section.path)
                }
                aria-label={label}
                aria-current={active ? "page" : undefined}
                className={ICON_ROW + rowTone(active)}
              >
                <Icon name={section.icon} size={17} />
                <Tooltip label={label} />
              </button>
            );
          }
          const showChildren = active && section.rail.length > 1;
          return (
            <div key={section.id}>
              {showGroupLabel && (
                <div className="mb-1 mt-5 px-3 ed-mono text-[9px] font-medium uppercase tracking-[0.14em] text-[var(--rail-text)]">
                  {navGroupLabel(section.group!, locale)}
                </div>
              )}
              <button
                type="button"
                onClick={() =>
                  onSectionNavigate
                    ? onSectionNavigate(section.path, section.id)
                    : go(section.path)
                }
                aria-current={active ? "page" : undefined}
                className={ROW + rowTone(active)}
              >
                <Icon name={section.icon} size={17} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium tracking-[-0.01em]">
                  {label}
                </span>
              </button>
              {showChildren && (
                <div className="mb-1 mt-0.5 space-y-0.5">
                  {section.rail.map((item) => {
                    const itemActive = itemMatchesPath(item, pathname);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => go(item.path)}
                        aria-current={itemActive ? "page" : undefined}
                        className={ROW + "min-h-9 pl-10 " + rowTone(itemActive)}
                      >
                        <span className="min-w-0 flex-1 truncate text-[12px]">
                          {navLabel(item.id, item.label, locale)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {isInstructor && (
          <>
            <div className="mx-2 my-2 h-px bg-[var(--rail-hairline)]" />
            {verticalId === "general" &&
              (collapsed ? (
                <button
                  type="button"
                  onClick={() => go("/studio")}
                  aria-label={STUDIO_LABEL[locale] ?? STUDIO_LABEL.en}
                  className={ICON_ROW + rowTone(false)}
                >
                  <Sparkles size={17} aria-hidden="true" />
                  <Tooltip label={STUDIO_LABEL[locale] ?? STUDIO_LABEL.en} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => go("/studio")}
                  className={ROW + rowTone(false)}
                >
                  <Sparkles size={16} aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate ed-display text-[12.5px] tracking-tight">
                    {STUDIO_LABEL[locale] ?? STUDIO_LABEL.en}
                  </span>
                </button>
              ))}
            {collapsed ? (
              <button
                type="button"
                onClick={() => go("/instructor")}
                aria-label={INSTRUCTOR_LABEL[locale] ?? INSTRUCTOR_LABEL.en}
                className={ICON_ROW + rowTone(false)}
              >
                <GraduationCap size={17} aria-hidden="true" />
                <Tooltip
                  label={INSTRUCTOR_LABEL[locale] ?? INSTRUCTOR_LABEL.en}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => go("/instructor")}
                className={ROW + rowTone(false)}
              >
                <GraduationCap size={16} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate ed-display text-[12.5px] tracking-tight">
                  {INSTRUCTOR_LABEL[locale] ?? INSTRUCTOR_LABEL.en}
                </span>
              </button>
            )}
          </>
        )}
      </nav>

      {/* Account cluster — bottom-left, like Claude's account chip. Collapsed:
          avatar + sign out only (expand for status, language, email). */}
      {consumerNav ? (
        collapsed ? (
          <div className="shrink-0 space-y-1 border-t border-[var(--rail-hairline)] px-2 py-2.5">
            <button
              type="button"
              onClick={openPlan}
              aria-label={hasAccess ? "Plan" : "Upgrade"}
              className={ICON_ROW + rowTone(false)}
            >
              <Sparkles size={16} aria-hidden="true" />
              <Tooltip label={hasAccess ? "Plan" : "Upgrade"} />
            </button>
            <button
              type="button"
              onClick={() => go("/profile")}
              aria-label="Settings"
              className={ICON_ROW + rowTone(pathname.startsWith("/profile"))}
            >
              <Icon name="settings" size={16} />
              <Tooltip label="Settings" />
            </button>
          </div>
        ) : (
          <div className="shrink-0 space-y-1 border-t border-[var(--rail-hairline)] px-3 py-3">
            <button
              type="button"
              onClick={openPlan}
              className={ROW + rowTone(false)}
            >
              <Sparkles size={16} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium tracking-[-0.01em]">
                {hasAccess ? "Plan" : "Upgrade"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => go("/profile")}
              className={ROW + rowTone(pathname.startsWith("/profile"))}
            >
              <Icon name="settings" size={16} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium tracking-[-0.01em]">
                Settings
              </span>
            </button>
          </div>
        )
      ) : collapsed ? (
        <div className="shrink-0 space-y-1 border-t border-[var(--rail-hairline)] px-2 py-2.5">
          <button
            type="button"
            onClick={() => go("/profile")}
            aria-label="Your profile"
            className="group relative mx-auto grid h-9 w-9 cursor-pointer place-items-center rounded-lg transition-colors duration-200 hover:bg-[var(--rail-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[var(--ink)] ed-mono text-[11px] text-[var(--paper)]">
              {userInitial}
            </span>
            <Tooltip
              label={
                hideAccountStatus || status.pending
                  ? "Your profile"
                  : status.label
              }
            />
          </button>
          <button
            type="button"
            onClick={onSignOut}
            aria-label={t("shell.signOut")}
            className={
              ICON_ROW +
              "h-9 w-9 text-[var(--rail-text)] hover:bg-[var(--rail-hover-bg)] hover:text-rose-500"
            }
          >
            <LogOut size={16} strokeWidth={2} />
            <Tooltip label={t("shell.signOut")} />
          </button>
        </div>
      ) : (
        <div className="shrink-0 space-y-2 border-t border-[var(--rail-hairline)] px-3 py-4">
          {!hideAccountStatus && !status.pending && (
            <button
              type="button"
              onClick={() => go("/profile")}
              className={`inline-flex h-8 w-full cursor-pointer items-center gap-1.5 rounded-full px-3 ed-mono text-[9px] tracking-wide ${status.className}`}
              aria-label={`Account status: ${status.label}`}
              title={status.label}
            >
              <StatusIcon size={12} aria-hidden />
              <span className="truncate">{status.label}</span>
            </button>
          )}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => go("/profile")}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded-lg px-1 py-1 text-left transition-colors duration-200 hover:bg-[var(--rail-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              aria-label="Your profile"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--ink)] ed-mono text-[11px] text-[var(--paper)]">
                {userInitial}
              </span>
              {userEmail && (
                <span className="min-w-0 flex-1 truncate text-[11px] text-[var(--rail-text)]">
                  {userEmail}
                </span>
              )}
            </button>
            <LanguageSelect openUp />
            <button
              type="button"
              onClick={onSignOut}
              aria-label={t("shell.signOut")}
              className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-lg text-[var(--rail-text)] transition-colors duration-200 hover:bg-[var(--rail-hover-bg)] hover:text-rose-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              <LogOut size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
